import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { FaArrowLeft, FaSave, FaTrash } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import api from '../utils/apiClient';
import { API_ENDPOINTS } from '../config/api';
import ImageUploader from '../components/common/ImageUploader';

export default function NFTEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Log user info for debugging
  console.log('NFTEdit - User from context:', user);
  console.log('NFTEdit - User from localStorage:', localStorage.getItem('user'));
  console.log('NFTEdit - isAdminSession:', localStorage.getItem('isAdminSession'));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [collections, setCollections] = useState([]);
  const [formData, setFormData] = useState({
    collectionId: '',
    name: '',
    description: '',
    image: '',
    rarity: 'Common',
    attributes: [],
    price: 0.08,
    currency: 'ETH',
    status: 'draft'
  });

  // Check if user is admin
  const adminCheckPerformed = useRef(false);

  useEffect(() => {
    // Only perform this check once to prevent infinite loops
    if (adminCheckPerformed.current) return;

    const checkAdminStatus = () => {
      // Check if we're in development mode
      if (import.meta.env.DEV) {
        console.log('Development mode detected, bypassing admin check');
        return true;
      }

      // First check user context
      if (user?.isAdmin) {
        console.log('User is admin via context');
        return true;
      }

      // If not in context, check localStorage directly
      try {
        const userStr = localStorage.getItem('user');
        if (userStr) {
          const localUser = JSON.parse(userStr);
          if (localUser && localUser.isAdmin) {
            console.log('User is admin via localStorage');
            return true;
          }
        }
      } catch (err) {
        console.error("Error parsing user data from localStorage", err);
      }

      // Check if we have an admin token
      const isAdminSession = localStorage.getItem('isAdminSession') === 'true';
      if (isAdminSession) {
        console.log('Admin session found in localStorage');
        return true;
      }

      console.log('User is not admin');
      return false;
    };

    adminCheckPerformed.current = true;

    if (!checkAdminStatus()) {
      toast.error('Access denied. Admin privileges required.');
      navigate('/');
    }
  }, [user, navigate]);

  // Fetch NFT data and collections
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Add admin headers for these requests
        const config = {
          headers: {
            'X-Admin-Request': 'true',
            'X-Debug-Admin': 'true'
          }
        };

        // Fetch NFT data
        const nftResponse = await api.get(API_ENDPOINTS.nft.nft(id), config);

        if (!nftResponse) {
          throw new Error('Failed to fetch NFT details');
        }

        // Fetch collections for dropdown
        const collectionsResponse = await api.get(API_ENDPOINTS.admin.nft.collections, config);
        const collectionsData = Array.isArray(collectionsResponse) ? collectionsResponse :
                              (collectionsResponse.data ? collectionsResponse.data : []);

        setCollections(collectionsData);

        // Set form data
        setFormData({
          collectionId: nftResponse.collectionId?._id || nftResponse.collectionId,
          name: nftResponse.name,
          description: nftResponse.description,
          image: nftResponse.image,
          rarity: nftResponse.rarity,
          attributes: nftResponse.attributes || [],
          price: nftResponse.price,
          currency: nftResponse.currency || 'ETH',
          totalSupply: nftResponse.totalSupply || 1,
          status: nftResponse.status
        });

        setLoading(false);
      } catch (error) {
        console.error('Error fetching NFT details:', error);
        toast.error('Failed to load NFT details');
        setLoading(false);
        navigate('/admin?tab=nfts');
      }
    };

    if (id) {
      fetchData();
    }
  }, [id, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'price' ? parseFloat(value) : value
    });
  };

  // Handle attribute change
  const handleAttributeChange = (index, field, value) => {
    const updatedAttributes = [...formData.attributes];
    updatedAttributes[index] = {
      ...updatedAttributes[index],
      [field]: field === 'value' ? Number(value) : value
    };
    setFormData({
      ...formData,
      attributes: updatedAttributes
    });
  };

  // Add new attribute
  const handleAddAttribute = () => {
    setFormData({
      ...formData,
      attributes: [
        ...formData.attributes,
        { trait_type: '', value: 0 }
      ]
    });
  };

  // Remove attribute
  const handleRemoveAttribute = (index) => {
    const updatedAttributes = [...formData.attributes];
    updatedAttributes.splice(index, 1);
    setFormData({
      ...formData,
      attributes: updatedAttributes
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSaving(true);

      // Handle numeric values
      const submitData = {
        ...formData,
        price: Number(formData.price),
        attributes: formData.attributes.map(attr => ({
          ...attr,
          value: typeof attr.value === 'string' && !isNaN(attr.value) ? Number(attr.value) : attr.value
        }))
      };

      console.log('Submitting NFT update with data:', submitData);
      const updateEndpoint = API_ENDPOINTS.admin.nft.updateNFT(id);
      console.log('Update endpoint:', updateEndpoint);

      // Add admin headers for this request
      const config = {
        headers: {
          'X-Admin-Request': 'true',
          'X-Debug-Admin': 'true'
        }
      };

      // Update NFT
      const response = await api.put(updateEndpoint, submitData, config);
      console.log('Update response:', response);
      toast.success('NFT updated successfully');

      // Navigate back to NFT details
      navigate(`/nft/${id}`);
    } catch (error) {
      console.error('Error updating NFT:', error);
      toast.error('Failed to update NFT: ' + (error.message || 'Unknown error'));
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this NFT? This action cannot be undone.')) {
      try {
        const deleteEndpoint = API_ENDPOINTS.admin.nft.deleteNFT(id);
        console.log('Delete endpoint:', deleteEndpoint);

        // Add admin headers for this request
        const config = {
          headers: {
            'X-Admin-Request': 'true',
            'X-Debug-Admin': 'true'
          }
        };

        const response = await api.delete(deleteEndpoint, config);
        console.log('Delete response:', response);
        toast.success('NFT deleted successfully');
        navigate('/nft-collection');
      } catch (error) {
        console.error('Error deleting NFT:', error);

        // Check for specific error about minted NFTs
        if (error.message && error.message.includes('Cannot delete NFT that has been minted')) {
          toast.error('Cannot delete NFT that has been minted. Only draft NFTs can be deleted.');

          // Offer a direct database deletion option
          if (window.confirm('Would you like to force delete this NFT directly from the database? This will remove the record completely.')) {
            try {
              // Use the force delete endpoint
              const forceDeleteEndpoint = API_ENDPOINTS.admin.nft.forceDeleteNFT(id);
              console.log('Force delete endpoint:', forceDeleteEndpoint);

              // Add special headers for force delete
              const forceConfig = {
                headers: {
                  'X-Admin-Request': 'true',
                  'X-Debug-Admin': 'true',
                  'X-Database-Override': 'true'
                }
              };

              const forceResponse = await api.delete(forceDeleteEndpoint, forceConfig);
              console.log('Force delete response:', forceResponse);
              toast.success('NFT force deleted successfully from database');
              navigate('/nft-collection');
            } catch (forceError) {
              console.error('Error force deleting NFT from database:', forceError);
              toast.error('Failed to force delete NFT: ' + (forceError.message || 'Unknown error'));
            }
          }
        } else {
          toast.error('Failed to delete NFT: ' + (error.message || 'Unknown error'));
        }
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-dark flex justify-center items-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-violet-500"></div>
      </div>
    );
  }

  // Allow editing regardless of status
  const isEditable = true;

  return (
    <div className="min-h-screen bg-dark">
      {/* Header */}
      <div className="max-w-7xl mx-auto px-4 py-6 flex justify-between items-center">
        <button
          onClick={() => navigate(`/nft/${id}`)}
          className="flex items-center text-gray-300 hover:text-white transition-colors"
        >
          <FaArrowLeft className="mr-2" /> Back to NFT
        </button>
        <div className="flex items-center space-x-4">
          <button
            onClick={handleDelete}
            className="flex items-center text-red-400 hover:text-red-300 transition-colors"
          >
            <FaTrash className="mr-2" /> Delete NFT
          </button>

          <button
            onClick={async () => {
              if (window.confirm('WARNING: This will FORCE DELETE the NFT directly from the database regardless of its status. This action cannot be undone. Are you absolutely sure?')) {
                try {
                  // Use the force delete endpoint
                  const forceDeleteEndpoint = API_ENDPOINTS.admin.nft.forceDeleteNFT(id);
                  console.log('Force delete endpoint:', forceDeleteEndpoint);

                  // Add special headers for force delete
                  const forceConfig = {
                    headers: {
                      'X-Admin-Request': 'true',
                      'X-Debug-Admin': 'true',
                      'X-Database-Override': 'true'
                    }
                  };

                  console.log('Force delete config:', forceConfig);

                  const forceResponse = await api.delete(forceDeleteEndpoint, forceConfig);
                  console.log('Force delete response:', forceResponse);
                  toast.success('NFT force deleted successfully from database');
                  navigate('/nft-collection');
                } catch (forceError) {
                  console.error('Error force deleting NFT from database:', forceError);
                  toast.error('Failed to force delete NFT: ' + (forceError.message || 'Unknown error'));
                }
              }
            }}
            className="flex items-center text-orange-500 hover:text-orange-400 transition-colors"
          >
            <FaTrash className="mr-2" /> Force Delete
          </button>
        </div>
      </div>

      {/* Edit Form */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-white mb-8">Edit NFT</h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column - Image and Basic Info */}
            <div className="space-y-6">
              <div>
                <ImageUploader
                  imageUrl={formData.image}
                  onImageUploaded={(imageUrl) => {
                    setFormData({
                      ...formData,
                      image: imageUrl
                    });
                  }}
                  uploadEndpoint={API_ENDPOINTS.admin.nft.uploadNFTImage}
                  label="NFT Image"
                  aspectRatio="1/1"
                  disabled={false}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Collection</label>
                <select
                  name="collectionId"
                  value={formData.collectionId}
                  onChange={handleInputChange}
                  required
                  className="w-full p-3 bg-dark-lighter border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                  disabled={false}
                >
                  <option value="">Select Collection</option>
                  {collections.map(collection => (
                    <option key={collection._id} value={collection._id}>
                      {collection.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Rarity</label>
                <select
                  name="rarity"
                  value={formData.rarity}
                  onChange={handleInputChange}
                  required
                  className="w-full p-3 bg-dark-lighter border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                  disabled={false}
                >
                  <option value="Common">Common</option>
                  <option value="Uncommon">Uncommon</option>
                  <option value="Rare">Rare</option>
                  <option value="Epic">Epic</option>
                  <option value="Legendary">Legendary</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Price</label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  step="0.01"
                  min="0"
                  required
                  className="w-full p-3 bg-dark-lighter border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Currency</label>
                <select
                  name="currency"
                  value={formData.currency}
                  onChange={handleInputChange}
                  required
                  className="w-full p-3 bg-dark-lighter border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                >
                  <option value="ETH">ETH</option>
                  <option value="IMX">IMX</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Total Supply (Editions)</label>
                <input
                  type="number"
                  name="totalSupply"
                  value={formData.totalSupply}
                  onChange={handleInputChange}
                  required
                  min="1"
                  step="1"
                  className="w-full p-3 bg-dark-lighter border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                  disabled={formData.status !== 'draft'} // Only allow editing if NFT is still a draft
                />
                <p className="text-xs text-gray-400 mt-1">
                  Number of copies (editions) that can be minted for this NFT.
                  {formData.status !== 'draft' && " Cannot be changed after minting has started."}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="w-full p-3 bg-dark-lighter border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                >
                  <option value="draft">Draft</option>
                  <option value="minting">Minting</option>
                  <option value="minted">Minted</option>
                  <option value="transferred">Transferred</option>
                  <option value="sold">Sold</option>
                </select>
                <p className="text-xs text-gray-400 mt-1">
                  NFTs with status "minting", "minted", "transferred", or "sold" will appear on the frontend.
                </p>
              </div>
            </div>

            {/* Right Column - Name, Description, Attributes */}
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full p-3 bg-dark-lighter border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="5"
                  className="w-full p-3 bg-dark-lighter border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                ></textarea>
              </div>
            </div>
          </div>

          {/* Attributes Section */}
          <div className="mt-8">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-white">Attributes</h2>
              <button
                type="button"
                onClick={handleAddAttribute}
                className="px-3 py-1 bg-violet-600 hover:bg-violet-700 text-white rounded-lg text-sm"
              >
                Add Attribute
              </button>
            </div>

            <div className="space-y-4">
              {formData.attributes.map((attr, index) => (
                <div key={index} className="grid grid-cols-5 gap-4 items-center">
                  <div className="col-span-2">
                    <input
                      type="text"
                      value={attr.trait_type}
                      onChange={(e) => handleAttributeChange(index, 'trait_type', e.target.value)}
                      placeholder="Trait Name"
                      className="w-full p-2 bg-dark-lighter border border-gray-700 rounded-lg"
                    />
                  </div>
                  <div className="col-span-2">
                    <input
                      type="number"
                      value={attr.value}
                      onChange={(e) => handleAttributeChange(index, 'value', e.target.value)}
                      placeholder="Value"
                      className="w-full p-2 bg-dark-lighter border border-gray-700 rounded-lg"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveAttribute(index)}
                    className="text-red-400 hover:text-red-300"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end mt-8">
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-3 bg-violet-600 hover:bg-violet-700 text-white rounded-lg flex items-center disabled:opacity-50"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <FaSave className="mr-2" /> Save Changes
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
